import { Component, Output, EventEmitter, Input, Inject } from "@angular/core";
import { combineLatest, forkJoin, Observable, of } from "rxjs";

import { ButtonActions } from "../../../../app.constants";
import { StepAction } from "../../entities/step-action-entity";
import { DocumentPaButton } from "../../entities/document-pa-button";
import { DocumentPersonneAvertie, EtatDocumentPa } from "../../entities/document-pa.entity";
import { SubjectRelay } from "../../../../shared/commands/subject-relay.class";
import { TypeDocumentPa } from "../../entities/document-pa.entity";
import { DocumentPaService } from "../../services/document-pa-service";
import { mergeMap } from "rxjs/operators";
import { DocumentEchange } from '../../entities/document-pa-echange';
import { DocEchangeUpdateStepOrder } from '../../entities/document-pa-doc-echange-update-order';

type RadioOption<T> = {
    value: T,
    label: string,
}

@Component({
    selector: "document-pa-echange",
    templateUrl: "document-pa-echange.html",
    styleUrls: ["document-pa-echange.css"],
})
export class DocumentPaEchange {

    @Output() actionRequested = new EventEmitter<StepAction>();
	
	isPaLoaded = false;
    currentPaInstance!: DocumentPersonneAvertie;
    documentPaEchange: DocEchangeUpdateStepOrder = new DocEchangeUpdateStepOrder(-1);
    documentsEchange: DocumentEchange[] = [];
    displayedColumns = ["libelle", "responsabilite", "remarque"];
	
	paEtats: typeof EtatDocumentPa = EtatDocumentPa;

    today = {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate()
    };

    private currentPaValue?: DocumentPersonneAvertie;

    typeOptions: RadioOption<TypeDocumentPa>[] = [
        { value: TypeDocumentPa.SUR_SITE, label: 'Sur site' },
        { value: TypeDocumentPa.A_DISTANCE, label: 'À distance' }
    ];

    actionButtons: DocumentPaButton[] = [
        new DocumentPaButton(ButtonActions.PREVIOUS, "Back", "btn btn-primary"),
        new DocumentPaButton(ButtonActions.CANCEL, "Liste des PA", "btn btn-danger"),
        new DocumentPaButton(ButtonActions.SAVE, "Sauvegarder maintenant", "btn btn-warning"),
        new DocumentPaButton(ButtonActions.NEXT, "Next", "btn btn-success"),
    ];

    private currentPa$ = new SubjectRelay<DocumentPersonneAvertie>();
    @Input() set currentPa(obs: Observable<DocumentPersonneAvertie> | undefined) {
        this.currentPa$.registerSource(obs);
    }

    constructor(@Inject(DocumentPaService) private documentPaService: DocumentPaService) {

        this.currentPa$.subscribe((currentPa) => {
            if (!currentPa) return;
			
			this.isPaLoaded = false;
			
			this.currentPaInstance = new DocumentPersonneAvertie();
            this.currentPaInstance = currentPa;
			this.currentPaInstance.duplicatedId = currentPa.duplicatedId;
			this.currentPaInstance.etat = currentPa.etat;

            const docs$ = this.documentPaService.getDocumentsEchange();
			const sourceId = this.currentPaInstance.duplicatedId
			    ? this.currentPaInstance.duplicatedId
			    : this.currentPaInstance.id;

			const stepData$ = this.documentPaService.getDocumentEchangeData(sourceId);

            const data$ = combineLatest([docs$, stepData$]).pipe(
                mergeMap(([docs, stepData]) =>
                    forkJoin({
                        docs: of(docs),
                        stepData: of(stepData)
                    })
                )
            );

            data$.subscribe(({ docs, stepData }) => {
                this.currentPaValue = currentPa;

                this.documentsEchange = docs;
				this.documentsEchange.forEach(doc => {
				    doc.checked = doc.customFields === 'preSelected';
				});
                this.documentPaEchange = new DocEchangeUpdateStepOrder(currentPa.id);
                this.documentPaEchange.presentationSite.type = TypeDocumentPa.SUR_SITE;

                if (stepData && stepData.documentEchanges && stepData.presentationSite) {
                    this.documentsEchange.forEach(doc => {
                        const saved = stepData.documentEchanges.find(x => x.id === doc.id);
                        doc.checked = saved?.checked ?? false;
                    });

                    this.documentPaEchange.presentationSite.id = stepData.presentationSite.id;
/*                    this.documentPaEchange.presentationSite.dateRealisation =
                        stepData.presentationSite.dateRealisation ??
                        [this.today.year, this.today.month, this.today.day];*/
					this.documentPaEchange.presentationSite.dateRealisation =
						    stepData.presentationSite.dateRealisation ?? null;

                    this.documentPaEchange.presentationSite.type =
                        stepData.presentationSite.type;

                    this.documentPaEchange.presentationSite.zoneAccesInterdit =
                        stepData.presentationSite.zoneAccesInterdit;

                    this.documentPaEchange.presentationSite.particulariteInstallation =
                        stepData.presentationSite.particulariteInstallation;

                    this.documentPaEchange.presentationSite.conduiteATenir =
                        stepData.presentationSite.conduiteATenir;
                }
            });
			this.evaluerFormulaire();
			this.isPaLoaded = true;
        });
    }

    performAction(requestedAction: ButtonActions): void {
        switch (requestedAction) {

            case ButtonActions.CANCEL:
            case ButtonActions.RETURN_TO_LIST:
                this.actionRequested.emit(new StepAction(null, ButtonActions.RETURN_TO_LIST));
                break;

            case ButtonActions.PREVIOUS: {
                this.actionRequested.emit(new StepAction(this.currentPaValue as DocumentPersonneAvertie, ButtonActions.PREVIOUS));
                break;
            }

            case ButtonActions.SAVE:
            case ButtonActions.SAVE:
                this.saveAndThen(ButtonActions.RETURN_TO_LIST);
                break;

            case ButtonActions.NEXT:
                this.saveAndThen(ButtonActions.NEXT);
                break;

            default:
            // rien à faire ici
        }
    }

    private buildUpdatePayload(): DocEchangeUpdateStepOrder {
        const payload = new DocEchangeUpdateStepOrder(this.currentPaInstance.id);

        payload.documentEchanges = this.documentsEchange
            .map(d => ({
                id: d.id,
                checked: d.checked ?? false
            }));

        payload.presentationSite = {
            id: this.documentPaEchange.presentationSite.id,
            dateRealisation: this.documentPaEchange.presentationSite.dateRealisation,
            type: this.documentPaEchange.presentationSite.type,
            zoneAccesInterdit: this.documentPaEchange.presentationSite.zoneAccesInterdit,
            particulariteInstallation: this.documentPaEchange.presentationSite.particulariteInstallation,
            conduiteATenir: this.documentPaEchange.presentationSite.conduiteATenir
        };

        return payload;
    }

    private saveAndThen(action: ButtonActions): void {
        const payload = this.buildUpdatePayload();

        this.documentPaService.updateDocEchangeStep(payload).subscribe({
            next: (response) => {
                if (this.currentPaInstance) {
                    this.currentPaInstance.lastCompletedStep = Math.max(
                        this.currentPaInstance.lastCompletedStep ?? 0,
                        6
                    );
                    this.currentPaInstance.id = response.id;
                }
                this.actionRequested.emit(new StepAction(this.currentPaInstance, action));
            }
        });
    }

	evaluerFormulaire(): void {
	    if (!this.actionButtons || this.actionButtons.length < 4) {
	        return;
	    }

	    this.actionButtons[2].disabled = false;
	    this.actionButtons[3].disabled = false;
	}
	
}